import React, { useState, useRef, useEffect } from "react";
import {
	TextField,
	Paper,
	MenuList,
	MenuItem,
	Popper,
	ClickAwayListener,
	makeStyles,
} from "@material-ui/core";
import {
	getMappableFieldsForContext,
	getPartialFieldAtCursor,
	insertFieldAtCursor,
} from "../utils/textareaAutocompletion";

const useStyles = makeStyles((theme) => ({
	textField: {
		width: "100%",
		"& .MuiInputBase-root": {
			fontFamily: "inherit",
			fontSize: "14px",
			lineHeight: 1.6,
		},
		"& .MuiInputBase-input": {
			alignItems: "flex-start",
		},
	},
	popper: {
		zIndex: 1300,
	},
	paper: {
		maxHeight: 200,
		overflow: "auto",
	},
	menuItem: {
		display: "flex",
		alignItems: "center",
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	selectedMenuItem: {
		backgroundColor: theme.palette.action.selected,
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	fieldIndicator: {
		color: "#53B3EA",
		marginRight: theme.spacing(1),
		fontWeight: "bold",
	},
	helpText: {
		marginTop: theme.spacing(0.5),
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
	},
	chip: {
		height: 20,
		fontSize: "0.75rem",
	},
}));

const TextareaWithAutocompletion = ({
	value,
	onChange,
	onBlur,
	placeholder,
	disabled,
	error,
	useCase,
	messageType,
	resources,
}) => {
	const classes = useStyles();
	const [suggestions, setSuggestions] = useState([]);
	const [partialField, setPartialField] = useState(null);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const textFieldRef = useRef(null);
	const inputRef = useRef(null);
	const [caretPosition, setCaretPosition] = useState({ top: 0, left: 0 });
	const anchorRef = useRef(null);
	const wrapperRef = useRef(null);
	const mirrorDivRef = useRef(null);

	// Helper to get caret coordinates in the textarea
	const updateCaretAnchor = () => {
		if (!inputRef.current || !wrapperRef.current || !mirrorDivRef.current)
			return;
		const textarea = inputRef.current;
		const mirrorDiv = mirrorDivRef.current;
		const textValue = textarea.value;
		const selectionStart = textarea.selectionStart;

		// Copy textarea styles to mirror
		const style = window.getComputedStyle(textarea);
		mirrorDiv.style.whiteSpace = "pre-wrap";
		mirrorDiv.style.wordWrap = "break-word";
		mirrorDiv.style.font = style.font;
		mirrorDiv.style.fontSize = style.fontSize;
		mirrorDiv.style.fontFamily = style.fontFamily;
		mirrorDiv.style.letterSpacing = style.letterSpacing;
		mirrorDiv.style.padding = style.padding;
		mirrorDiv.style.border = style.border;
		mirrorDiv.style.boxSizing = style.boxSizing;
		mirrorDiv.style.width = style.width;
		mirrorDiv.style.lineHeight = style.lineHeight;
		mirrorDiv.style.position = "absolute";
		mirrorDiv.style.visibility = "hidden";
		mirrorDiv.style.top = "0px";
		mirrorDiv.style.left = "0px";

		// Clear previous content
		mirrorDiv.innerHTML = "";

		// Set mirror content up to caret
		const before = textValue.substring(0, selectionStart);
		const after = textValue.substring(selectionStart);
		mirrorDiv.textContent = before;

		// Create a span for the caret
		const caretSpan = document.createElement("span");
		caretSpan.textContent = after.length === 0 ? "\u200b" : after[0];
		mirrorDiv.appendChild(caretSpan);

		// Get caret position
		const rect = caretSpan.getBoundingClientRect();
		const mirrorRect = mirrorDiv.getBoundingClientRect();
		const top = rect.top - mirrorRect.top;
		const left = rect.left - mirrorRect.left;
		setCaretPosition({
			top: top + textarea.scrollTop,
			left: left + textarea.scrollLeft,
		});
	};

	const open = suggestions.length > 0;

	const handleInputChange = (event) => {
		const newValue = event.target.value;
		const cursorPosition = event.target.selectionStart;

		onChange(newValue);

		const partial = getPartialFieldAtCursor(newValue, cursorPosition);

		if (partial) {
			const availableFields = getMappableFieldsForContext(
				useCase?.useCase,
				messageType
			);

			const filteredFields = availableFields.filter((field) => {
				const fieldName = field.replace(/#/g, "");
				return fieldName
					.toLowerCase()
					.startsWith(partial.partialField.toLowerCase());
			});

			if (filteredFields.length > 0) {
				setSuggestions(filteredFields);
				setSelectedIndex(0);
				setPartialField(partial);
			} else {
				closeSuggestions();
			}
		} else {
			closeSuggestions();
		}
	};

	const handleKeyDown = (event) => {
		if (event.key === "Escape") {
			if (suggestions.length > 0) {
				closeSuggestions();
				event.preventDefault();
				event.stopPropagation();
			}
			return;
		}
		if (open) {
			if (event.key === "ArrowDown") {
				setSelectedIndex((prevIndex) =>
					prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0
				);
				event.preventDefault();
			} else if (event.key === "ArrowUp") {
				setSelectedIndex((prevIndex) =>
					prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1
				);
				event.preventDefault();
			} else if (event.key === "Enter" && suggestions.length > 0) {
				handleSuggestionSelect(suggestions[selectedIndex]);
				event.preventDefault();
			}
		}
	};

	const handleSuggestionSelect = (field) => {
		if (!inputRef.current || !partialField) return;

		const cursorPosition = inputRef.current.selectionStart;
		const result = insertFieldAtCursor(value, cursorPosition, field);

		onChange(result.text);

		// Set cursor position after the change
		setTimeout(() => {
			if (inputRef.current) {
				inputRef.current.setSelectionRange(
					result.cursorPosition,
					result.cursorPosition
				);
				inputRef.current.focus();
			}
		}, 0);

		closeSuggestions();
	};

	const closeSuggestions = () => {
		setSuggestions([]);
		setPartialField(null);
		setSelectedIndex(0);
	};

	const handleBlur = (event) => {
		// Small delay to allow clicking on suggestions
		setTimeout(() => {
			closeSuggestions();
		}, 150);

		if (onBlur) {
			onBlur(event);
		}
	};

	useEffect(() => {
		if (open) {
			updateCaretAnchor();
		}
	}, [open, value, selectedIndex]);

	return (
		<div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
			<TextField
				ref={textFieldRef}
				inputRef={inputRef}
				className={classes.textField}
				multiline
				minRows={8}
				maxRows={12}
				variant="outlined"
				value={value || ""}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onBlur={handleBlur}
				placeholder={placeholder}
				disabled={disabled}
				error={error}
				inputProps={{
					style: {
						resize: "vertical",
					},
				}}
			/>
			<div
				ref={mirrorDivRef}
				style={{
					position: "absolute",
					visibility: "hidden",
					pointerEvents: "none",
					height: 0,
					overflow: "hidden",
				}}
			/>
			<span
				ref={anchorRef}
				style={{
					position: "absolute",
					top: caretPosition.top,
					left: caretPosition.left,
					width: 1,
					height: "1em",
					pointerEvents: "none",
				}}
			/>
			<Popper
				open={open}
				anchorEl={anchorRef.current}
				placement="bottom-start"
				className={classes.popper}
				modifiers={{
					flip: {
						enabled: true,
					},
					preventOverflow: {
						enabled: true,
						boundariesElement: "scrollParent",
					},
				}}
			>
				<ClickAwayListener onClickAway={closeSuggestions}>
					<Paper className={classes.paper}>
						<MenuList>
							{suggestions.map((field, index) => (
								<MenuItem
									key={field}
									className={`${classes.menuItem} ${
										index === selectedIndex ? classes.selectedMenuItem : ""
									}`}
									selected={index === selectedIndex}
									onClick={() => handleSuggestionSelect(field)}
									onMouseEnter={() => setSelectedIndex(index)}
									ref={
										index === selectedIndex
											? (el) => {
													if (el) {
														el.scrollIntoView({ block: "nearest" });
													}
											  }
											: null
									}
								>
									<span className={classes.fieldIndicator}>#</span>
									<span>{field.replace(/#/g, "")}</span>
								</MenuItem>
							))}
						</MenuList>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</div>
	);
};

export default TextareaWithAutocompletion;
